/**
 * @file MemberDeletionRequests.tsx
 * @brief Company → Employees: members who asked for their account to be
 *        deleted (B7.2), each with Remove.
 *
 * @details
 * Remove is the existing DELETE /api/company/users/:id, which also marks the
 * request done (offboarding: the member's personal data erased, the
 * company's meetings kept). Hidden when there are none, or before the
 * backend has the route.
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { useDeleteEmployee } from "@/features/employees/api";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { formatDate } from "@/shared/utils/formatters";
import type { MemberDeletionRequest } from "./accountDeletion";
import { companyDeletionKey, useCompanyDeletion } from "./api";

export function MemberDeletionRequests() {
  const info = useCompanyDeletion(true);
  const remove = useDeleteEmployee();
  const qc = useQueryClient();
  const [target, setTarget] = useState<MemberDeletionRequest | null>(null);
  const requests = info.data?.memberRequests ?? [];
  if (requests.length === 0) return null;

  const confirm = async (req: MemberDeletionRequest) => {
    try {
      await remove.mutateAsync(req.userId);
      void qc.invalidateQueries({ queryKey: companyDeletionKey });
      toast({ variant: "success", title: "Employee removed", description: `${req.email || req.name}'s account is being deleted.` });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't remove the employee",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <>
      <GridBand>
        <p className="text-sm font-medium">Deletion requests</p>
        <p className="mt-1 text-xs text-muted-foreground">
          These people asked for their account to be deleted. Remove them within 30 days; the company keeps its meetings.
        </p>
        <ul className="mt-3 divide-y divide-border/60">
          {requests.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium">{r.name}</span>
                {r.email ? <span className="text-muted-foreground"> · {r.email}</span> : null}
                <span className="block text-xs text-muted-foreground">
                  {r.requestedAt ? `Asked on ${formatDate(r.requestedAt)}` : "Asked"}
                  {r.source === "web_public" ? " from the website" : ""}
                  {r.dueAt ? ` · due ${formatDate(r.dueAt)}` : ""}
                  {r.reason ? ` · “${r.reason}”` : ""}
                </span>
              </span>
              <Button type="button" size="sm" variant="destructive" onClick={() => setTarget(r)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </GridBand>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {target?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {target?.email || target?.name} loses access now and their personal data is deleted within 30 days.
              Meetings that belong to the company stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => target && void confirm(target)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
